/**
 * Shared clip view page (public with auth for first download)
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Play, Download, Clock, Zap, Loader2, Lock, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { shareAPI } from '../services/api';
import { useAuthStore } from '../store';
import { toast } from 'sonner';

export default function SharedClip() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  
  const [loading, setLoading] = useState(true);
  const [clip, setClip] = useState(null);
  const [error, setError] = useState(null);
  const [needsAuth, setNeedsAuth] = useState(false);
  
  useEffect(() => {
    fetchSharedClip();
  }, [token, isAuthenticated]); // fetchSharedClip is defined below and stable
  
  const fetchSharedClip = async () => {
    try {
      setLoading(true);
      const response = await shareAPI.getSharedClip(token);
      setClip(response.data.clip);
      setError(null);
      setNeedsAuth(false);
    } catch (err) {
      if (err.response?.status === 401) {
        setNeedsAuth(true);
      } else if (err.response?.status === 410) {
        setError('Este enlace ha expirado');
      } else if (err.response?.status === 404) {
        setError('Enlace no encontrado');
      } else {
        setError('Error cargando el clip');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleDownload = () => {
    if (clip?.download_url) {
      window.open(clip.download_url, '_blank');
    }
  };
  
  const handleLogin = () => {
    // Store current URL to redirect back after login
    sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
    navigate('/auth');
  };
  
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
            <Play className="w-5 h-5 text-primary-foreground fill-current" />
          </div>
          <span className="font-display font-bold text-xl">Clipper</span>
        </div>
        
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto" />
            <p className="text-muted-foreground mt-4">Cargando replay...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <AlertCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">{error}</h2>
            <Button
              variant="secondary"
              onClick={() => navigate('/')}
              className="mt-4"
            >
              Ir al inicio
            </Button>
          </div>
        ) : needsAuth ? (
          <div className="glass rounded-2xl p-6 text-center">
            <Lock className="w-16 h-16 text-primary mx-auto mb-4" />
            <h2 className="font-display font-bold text-xl mb-2">
              Inicia sesión para ver
            </h2>
            <p className="text-muted-foreground mb-6">
              La primera descarga requiere que inicies sesión en Clipper
            </p>
            <Button
              className="w-full h-12 glow-primary"
              onClick={handleLogin}
              data-testid="login-to-view-btn"
            >
              Iniciar sesión
            </Button>
          </div>
        ) : clip ? (
          <div className="glass rounded-2xl p-6">
            {/* Video player */}
            <div className="video-container mb-6 bg-black rounded-xl overflow-hidden">
              {clip.download_url ? (
                <video
                  controls
                  className="w-full h-full object-contain"
                  src={clip.download_url}
                  style={{ maxHeight: '250px' }}
                  onLoadedMetadata={(e) => {
                    // Set playback speed for fast clips
                    if (clip.is_fast) {
                      e.target.playbackRate = 3.0;
                    }
                  }}
                >
                  Tu navegador no soporta video HTML5
                </video>
              ) : (
                <div className="text-center py-8">
                  {clip.is_fast ? (
                    <Zap className="w-12 h-12 text-primary mx-auto mb-2" />
                  ) : (
                    <Clock className="w-12 h-12 text-primary mx-auto mb-2" />
                  )}
                  <p className="font-display font-bold text-2xl">
                    {clip.is_fast ? '90s → 30s (3x)' : '30s'} Replay
                  </p>
                </div>
              )}
            </div>
            
            {/* Speed indicator for fast clips */}
            {clip.is_fast && clip.download_url && (
              <div className="text-center text-sm text-primary bg-primary/10 rounded-lg py-2 mb-4">
                ⚡ Reproduciendo a velocidad 3x
              </div>
            )}
            
            {/* Info */}
            <div className="text-sm text-muted-foreground space-y-2 mb-6">
              <p>
                <span className="text-foreground font-medium">Club:</span>{' '}
                {clip.club_name}
              </p>
              <p>
                <span className="text-foreground font-medium">Fecha:</span>{' '}
                {new Date(clip.created_at).toLocaleDateString('es-MX', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            </div>
            
            {/* Download button */}
            <Button
              className="w-full h-14 text-lg font-bold uppercase tracking-wider glow-primary"
              onClick={handleDownload}
              disabled={!clip.download_url}
              data-testid="download-shared-clip-btn"
            >
              <Download className="w-5 h-5 mr-2" />
              Descargar
            </Button>
            
            {/* CTA */}
            <div className="mt-6 pt-6 border-t border-white/10 text-center">
              <p className="text-sm text-muted-foreground mb-3">
                ¿Quieres capturar tus propios replays?
              </p>
              <Button
                variant="secondary"
                onClick={() => navigate('/')}
              >
                Conoce Clipper
              </Button>
            </div>
          </div>
        ) : null}
      </motion.div>
    </div>
  );
}
